<template>
  <tr>
    <template v-if="isEditing">
      <!-- Edit Mode -->
      <td>{{ index }}</td>
      <td class="text-right">
        <b-form-input
          v-model.number="editData.received_amount"
          type="number"
          size="sm"
          class="text-right"
          :disabled="isUpdating"
          :class="{ 'border-danger': editData.received_amount !== null && editData.received_amount <= 0 }"
        />
      </td>
      <td>
        <b-form-select
          v-model="editData.payment_mode"
          :options="paymentModeOptions"
          :disabled="isUpdating"
          size="sm"
          value-field="value"
          text-field="text"
        >
          <template #first>
            <b-form-select-option
              :value="null"
              disabled
            >
              Payment Mode
            </b-form-select-option>
          </template>
        </b-form-select>
      </td>
      <td>
        <b-badge
          :variant="getPaymentStatusVariant(receivedAmount.payment_status)"
          class="text-capitalize"
        >
          {{ getPaymentStatusName(receivedAmount.payment_status) }}
        </b-badge>
      </td>
      <td>
        <span>{{ receivedAmount.invoice_number || '-' }}</span>
      </td>
      <td>
        <span>{{ receivedAmount.party_name || '-' }}</span>
      </td>
      <td>
        <b-form-input
          v-model="editData.remarks"
          size="sm"
          placeholder="Remarks"
          :disabled="isUpdating"
        />
      </td>
      <td>
        <div style="font-size: 0.75rem;">
          <div>{{ receivedAmount.created_at ? formatedDate(receivedAmount.created_at, 'DD-MM-YYYY LT') : '-' }}</div>
          <div class="text-primary">
            {{ receivedAmount.created_by_name || '-' }}
          </div>
        </div>
      </td>
      <td>
        <b-spinner
          v-if="isUpdating"
          small
          variant="success"
          class="mr-50"
        />
        <feather-icon
          v-else
          icon="CheckIcon"
          size="18"
          class="mr-50 cursor-pointer text-success"
          :class="{ 'text-muted': !isValidEdit }"
          @click="isValidEdit && handleUpdate()"
        />
        <feather-icon
          icon="XIcon"
          size="18"
          class="cursor-pointer"
          :class="{ 'text-muted': isUpdating }"
          @click="!isUpdating && handleCancelEdit()"
        />
      </td>
    </template>
    <template v-else>
      <!-- View Mode -->
      <td>{{ index }}</td>
      <td class="text-right">
        {{ formatAmount(receivedAmount.received_amount) }}
      </td>
      <td>
        <b-badge
          :variant="getPaymentModeVariant(receivedAmount.payment_mode)"
          class="text-capitalize"
        >
          {{ getPaymentModeName(receivedAmount.payment_mode) }}
        </b-badge>
      </td>
      <td>
        <b-badge
          :variant="getPaymentStatusVariant(receivedAmount.payment_status)"
          class="text-capitalize"
        >
          {{ getPaymentStatusName(receivedAmount.payment_status) }}
        </b-badge>
      </td>
      <td>
        <span>{{ receivedAmount.invoice_number || '-' }}</span>
      </td>
      <td>
        <span>{{ receivedAmount.party_name || '-' }}</span>
      </td>
      <td>
        <span>{{ receivedAmount.remarks || '-' }}</span>
      </td>
      <td>
        <div style="font-size: 0.75rem;">
          <div>{{ receivedAmount.created_at ? formatedDate(receivedAmount.created_at, 'DD-MM-YYYY LT') : '-' }}</div>
          <div class="text-primary">
            {{ receivedAmount.created_by_name || '-' }}
          </div>
        </div>
      </td><td>
        <a
          href="javascript:void(0)"
          class="mr-75 text-info"
          :class="{ 'disabled-link': isDeleting || isUpdating }"
          @click="(isDeleting || isUpdating) ? null : handleEdit()"
        >
          <feather-icon
            icon="EditIcon"
            size="16"
          />
        </a>
        <a
          href="javascript:void(0)"
          class="text-danger"
          :class="{ 'disabled-link': isDeleting || isUpdating }"
          @click="(isDeleting || isUpdating) ? null : handleDelete()"
        >
          <b-spinner
            v-if="isDeleting"
            small
            variant="danger"
            class="mr-25"
          />
          <feather-icon
            v-else
            icon="TrashIcon"
            size="16"
          />
        </a>
      </td>
    </template>
  </tr>
</template>

<script>
import {
  BFormInput,
  BSpinner,
  BBadge,
  BFormSelect,
  BFormSelectOption,
} from 'bootstrap-vue'
import axios from 'axios'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import dateMixin from '@/mixins/dateMixin'
import formatMixin from '@/mixins/formatMixin'

export default {
  components: {
    BFormInput,
    BSpinner,
    BBadge,
    BFormSelect,
    BFormSelectOption,
  },
  mixins: [dateMixin, formatMixin],
  props: {
    receivedAmount: {
      type: Object,
      required: true,
    },
    index: {
      type: Number,
      required: true,
    },
    paymentModeOptions: {
      type: Array,
      required: true,
    },
    paymentStatusOptions: {
      type: Array,
      required: true,
    },
  },
  data() {
    return {
      isEditing: false,
      isUpdating: false,
      isDeleting: false,
      editData: {
        id: null,
        received_amount: null,
        payment_mode: null,
        payment_status: 'paid',
        remarks: '',
      },
    }
  },
  computed: {
    isValidEdit() {
      return (
        this.editData.received_amount
        && this.editData.received_amount > 0
        && this.editData.payment_mode
      )
    },
  },
  methods: {
    handleEdit() {
      this.isEditing = true
      this.editData = {
        id: this.receivedAmount.id,
        received_amount: this.receivedAmount.received_amount,
        payment_mode: this.receivedAmount.payment_mode,
        payment_status: this.receivedAmount.payment_status || 'paid',
        remarks: this.receivedAmount.remarks || '',
      }
      this.$emit('edit', this.receivedAmount.id)
    },

    handleCancelEdit() {
      this.isEditing = false
      this.editData = {
        id: null,
        received_amount: null,
        payment_mode: null,
        payment_status: 'paid',
        remarks: '',
      }
      this.$emit('cancel-edit')
    },

    async handleUpdate() {
      if (!this.isValidEdit || this.isUpdating) return

      this.isUpdating = true
      try {
        const formData = {
          received_amount: this.editData.received_amount,
          payment_mode: this.editData.payment_mode,
          payment_status: this.editData.payment_status,
          remarks: this.editData.remarks,
        }

        await axios.patch(`/operations/received_amount/${this.editData.id}/`, formData)

        this.isEditing = false
        this.editData = {
          id: null,
          received_amount: null,
          payment_mode: null,
          payment_status: 'paid',
          remarks: '',
        }

        // Show success toast
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Received amount updated successfully',
            icon: 'CheckIcon',
            variant: 'success',
          },
        })

        // Emit to parent to refresh list
        this.$emit('updated')
      } catch (error) {
        // Show error notification
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Error',
            text: error.response?.data?.message || 'Failed to update received amount',
            icon: 'AlertCircleIcon',
            variant: 'danger',
          },
        })
      } finally {
        this.isUpdating = false
      }
    },

    handleDelete() {
      this.$bvModal
        .msgBoxConfirm('You won\'t be able to revert this!', {
          title: 'Are you sure?',
          size: 'sm',
          okVariant: 'danger',
          okTitle: this.isDeleting ? 'Deleting...' : 'Yes, delete it!',
          cancelTitle: 'Cancel',
          cancelVariant: 'outline-secondary',
          hideHeaderClose: false,
          centered: true,
          okDisabled: this.isDeleting,
          cancelDisabled: this.isDeleting,
        })
        .then(async value => {
          if (value) {
            await this.confirmDelete()
          }
        })
    },

    async confirmDelete() {
      this.isDeleting = true
      try {
        await axios.delete(`/operations/received_amount/${this.receivedAmount.id}/`)

        // Show success toast
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Received amount deleted successfully',
            icon: 'CheckIcon',
            variant: 'success',
          },
        })

        // Emit to parent to refresh list
        this.$emit('deleted')
      } catch (error) {
        // Show error notification
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Error',
            text: error.response?.data?.message || 'Failed to delete received amount',
            icon: 'AlertCircleIcon',
            variant: 'danger',
          },
        })
      } finally {
        this.isDeleting = false
      }
    },

    getPaymentModeName(mode) {
      const paymentMode = this.paymentModeOptions.find(m => m.value === mode)
      return paymentMode ? paymentMode.text : mode
    },

    getPaymentStatusName(status) {
      const paymentStatus = this.paymentStatusOptions.find(s => s.value === status)
      return paymentStatus ? paymentStatus.text : status
    },

    getPaymentModeVariant(mode) {
      const variants = {
        cash: 'success',
        upi: 'primary',
        cheque: 'info',
        rgts: 'warning',
        credit_note: 'secondary',
        challan_converted_bill: 'dark',
        loan: 'danger',
        mr_handover: 'info',
      }
      return variants[mode] || 'secondary'
    },

    getPaymentStatusVariant(status) {
      const variants = {
        paid: 'success',
        unpaid: 'danger',
        partially_paid: 'warning',
      }
      return variants[status] || 'secondary'
    },

    formatAmount(amount) {
      if (!amount && amount !== 0) return '0'
      return parseFloat(amount).toLocaleString('en-IN', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })
    },
  },
}
</script>

<style lang="scss" scoped>
/* Border danger for validation */
.border-danger {
  border-color: #ea5455 !important;
}

/* Icon links styling */
a.text-info:hover {
  color: #0056b3 !important;
  text-decoration: none;
}

a.text-danger:hover {
  color: #c82333 !important;
  text-decoration: none;
}

/* Disabled link styling */
.disabled-link {
  pointer-events: none;
  opacity: 0.5;
  cursor: not-allowed;
}

/* Table row color classes */
.table-success {
  background-color: #d4edda !important;
}

.table-danger {
  background-color: #f8d7da !important;
}

.table-warning {
  background-color: #fff3cd !important;
}

/* Cursor pointer for clickable icons */
.cursor-pointer {
  cursor: pointer;
}

/* Badge styling */
.badge {
  padding: 0.35rem 0.5rem;
  font-size: 0.75rem;
}

/* Small form controls */
.form-control-sm {
  height: calc(1.5em + 0.5rem + 2px);
  padding: 0.25rem 0.5rem;
  font-size: 0.875rem;
}

.mr-50 {
  margin-right: 0.5rem !important;
}

.mr-75 {
  margin-right: 0.75rem !important;
}

.text-muted {
  opacity: 0.5;
}
</style>
